import { isEqual } from "lodash";
import {
  BenefitLineTieBreak,
  BenefitSourceBlock,
  ISelectionPriority,
  ISelectionRule,
  ISelectionRuleSave,
  ISelectionRulesSave,
  ISelectionRulesView,
  SelectionRulesMode,
} from "types/BenefitSelection/BenefitSelection";

/**
 * CLE-2802 — "2 · Selection rules" as the card edits it, and how it becomes `selectionRules` on the
 * PUT (spec §6).
 *
 * 🚨 THE ARRAY'S ORDER IS THE POLICY. The PUT carries no position; the first rule covering a check's
 * payer and plan is the only one used, and Default is always last. So a move is the edit, and
 * `orderRules` is applied on every change so Default can never be anywhere else.
 */

export type RuleScope = "shared" | "professional";

export interface ISelectionRuleForm {
  /** A React key only. The PUT never carries it (the API ignores ids and replaces every row). */
  key: string;
  isDefault: boolean;
  payerIds: number[];
  planKeywords: string[];
  networkStatus: "InNetwork" | "OutOfNetwork";
  priorities: ISelectionPriority[];
}

export interface ISelectionRulesForm {
  mode: SelectionRulesMode;
  configured: boolean;
  ignoreKeywords: string[];
  shared: ISelectionRuleForm[];
  professional: ISelectionRuleForm[];
  /**
   * What the GET returned, as a save body. Kept so an account on the BUILT-IN defaults
   * (`configured = false`) is not given stored rows by a save that only changed its service line —
   * see `selectionRulesForSave`.
   */
  loaded: ISelectionRulesSave;
}

export const TIE_BREAK_OPTIONS: Array<{
  label: string;
  value: BenefitLineTieBreak;
}> = [
  { label: "Highest", value: "HIGHEST" },
  { label: "Lowest", value: "LOWEST" },
  { label: "First", value: "FIRST" },
];

export const tieBreakLabel = (value: BenefitLineTieBreak): string =>
  TIE_BREAK_OPTIONS.find((option) => option.value === value)?.label ?? value;

export const NETWORK_OPTIONS = [
  { label: "In network", value: "InNetwork" },
  { label: "Out of network", value: "OutOfNetwork" },
];

export const networkLabel = (value: string): string =>
  value === "OutOfNetwork" ? "Out of network" : "In network";

/** The physician tab's service lines, in the mockup's words. */
export const PHYSICIAN_SOURCES: Array<{
  label: string;
  value: BenefitSourceBlock;
}> = [
  { label: "Surgical (physician outpatient)", value: "SURGICAL" },
  { label: "MRI/CT", value: "MRI_CT" },
];

export const sourceLabel = (value: BenefitSourceBlock | null): string => {
  if (!value) return "—";
  return (
    PHYSICIAN_SOURCES.find((option) => option.value === value)?.label ?? value
  );
};

const ACRONYMS = new Set(["MRI", "CT", "ASC", "PPO", "HMO", "EPO", "POS"]);

/**
 * A vocabulary role in words: `MRI_PHYSICIAN_LINE` → "MRI physician line". The screen never shows
 * the constant, matching Eligibility › Line Type Definitions.
 */
export const lineRoleLabel = (role: string): string =>
  role
    .split("_")
    .filter(Boolean)
    .map((word, index) => {
      if (ACRONYMS.has(word.toUpperCase())) return word.toUpperCase();
      const lower = word.toLowerCase();
      return index === 0
        ? lower.charAt(0).toUpperCase() + lower.slice(1)
        : lower;
    })
    .join(" ");

/**
 * The fixed "Otherwise" row, per tab — configuration nobody edits, so it is text. CLE-2895: every rule
 * ends in an implicit catch-all — every remaining line, the highest — that carries none of the
 * priorities' "doesn't contain" words.
 */
export const OTHERWISE_TEXT: Record<RuleScope, string> = {
  shared:
    "If none match, use the highest value from every remaining line — including lines a priority said to skip. If there are no lines, use the payer's summary fields.",
  professional:
    "If none match, use the highest value from every remaining line — including lines a priority said to skip.",
};

/** The dialog's Otherwise box. */
export const OTHERWISE_HELP: Record<RuleScope, string> = {
  shared:
    "If none match, use the highest value from every remaining line — including lines a priority said to skip. If there are no lines, use the payer's summary fields.",
  professional:
    "If none match, use the highest value from every remaining line — including lines a priority said to skip.",
};

/** What the Default card says under its name, in place of a plans line. */
export const DEFAULT_RULE_TEXT =
  "Covers payers and plans no rule above names. Only used when no other rule matches the payer and plan.";

/** The sentence under the rule list — about the tab, not about any one rule. */
export const FOOTER_TEXT: Record<RuleScope, string> = {
  shared:
    'Rules apply to copay, co-insurance, deductible and out-of-pocket. "If several" picks among copay and co-insurance lines — dollar amount for copay, percent for co-insurance, "First" in the payer\'s order; deductible and out-of-pocket keep their own selection.',
  professional:
    'A line type matches the wording each payer uses for that kind of line, defined once on Eligibility › Line Type Definitions. "Highest" compares the dollar amount.',
};

let ruleKeySeq = 0;
export const nextRuleKey = (): string => {
  ruleKeySeq += 1;
  return `rule-${ruleKeySeq}`;
};

/**
 * Keywords as the API stores them: trimmed, non-empty, and each stated once. Matching is
 * case-insensitive, so `bjc` beside `BJC` would be one word stated twice.
 */
export const normaliseKeywords = (values: string[] | undefined): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  (values ?? []).forEach((raw) => {
    const value = raw.trim();
    if (!value || seen.has(value.toUpperCase())) return;
    seen.add(value.toUpperCase());
    result.push(value);
  });
  return result;
};

/** A new priority. The physician tab's opens on the Surgical service line, as in the mockup. */
export const emptyPriority = (scope: RuleScope): ISelectionPriority => ({
  source: scope === "professional" ? "SURGICAL" : null,
  lineRole: null,
  matchKeywords: [],
  excludeKeywords: [],
  tieBreak: "HIGHEST",
});

export const emptyRule = (scope: RuleScope): ISelectionRuleForm => ({
  key: nextRuleKey(),
  isDefault: false,
  payerIds: [],
  planKeywords: [],
  networkStatus: "InNetwork",
  priorities: [emptyPriority(scope)],
});

/** Default last, everything else in the order it was given. At most one Default survives. */
export const orderRules = <T extends { isDefault: boolean }>(
  rules: T[]
): T[] => {
  const defaults = rules.filter((rule) => rule.isDefault);
  return [...rules.filter((rule) => !rule.isDefault), ...defaults.slice(0, 1)];
};

/**
 * One priority as the PUT carries it. 🚨 THE SHARED TAB HAS NO SERVICE LINE OR LINE TYPE — both are
 * forced to null there rather than trusted from a draft, because the API reads a non-null value as a
 * physician-only criterion.
 */
const toPrioritySave = (
  priority: ISelectionPriority,
  scope: RuleScope
): ISelectionPriority => ({
  source: scope === "professional" ? priority.source ?? null : null,
  lineRole:
    scope === "professional" && priority.lineRole ? priority.lineRole : null,
  matchKeywords: normaliseKeywords(priority.matchKeywords),
  excludeKeywords: normaliseKeywords(priority.excludeKeywords),
  tieBreak: priority.tieBreak,
});

/** One rule as the PUT carries it. Default is never scoped — "payers and plans no rule above covers". */
export const toRuleSave = (
  rule: ISelectionRuleForm | ISelectionRule,
  scope: RuleScope
): ISelectionRuleSave => ({
  isDefault: rule.isDefault,
  payerIds: rule.isDefault ? [] : [...rule.payerIds],
  planKeywords: rule.isDefault ? [] : normaliseKeywords(rule.planKeywords),
  networkStatus: rule.networkStatus,
  priorities: rule.priorities.map((priority) =>
    toPrioritySave(priority, scope)
  ),
});

const toRulesSave = (source: {
  ignoreKeywords: string[];
  shared: Array<ISelectionRuleForm | ISelectionRule>;
  professional: Array<ISelectionRuleForm | ISelectionRule>;
}): ISelectionRulesSave => ({
  ignoreKeywords: normaliseKeywords(source.ignoreKeywords),
  shared: orderRules(source.shared).map((rule) => toRuleSave(rule, "shared")),
  professional: orderRules(source.professional).map((rule) =>
    toRuleSave(rule, "professional")
  ),
});

export const toSelectionRulesForm = (
  view: ISelectionRulesView
): ISelectionRulesForm => {
  const toRuleForm = (rule: ISelectionRule): ISelectionRuleForm => ({
    key: rule.id ?? nextRuleKey(),
    isDefault: rule.isDefault,
    payerIds: [...rule.payerIds],
    planKeywords: [...rule.planKeywords],
    networkStatus: rule.networkStatus,
    priorities: rule.priorities.map((priority) => ({
      ...priority,
      matchKeywords: [...priority.matchKeywords],
      excludeKeywords: [...priority.excludeKeywords],
    })),
  });
  return {
    mode: view.mode,
    configured: view.configured,
    ignoreKeywords: [...view.ignoreKeywords],
    shared: orderRules(view.shared).map(toRuleForm),
    professional: orderRules(view.professional).map(toRuleForm),
    loaded: toRulesSave(view),
  };
};

/**
 * `selectionRules` for the PUT, or undefined to leave the stored rules alone.
 *
 * 🚨 SENT ONLY WHILE THE STEP IS ON SCREEN (mode ON). With the flag OFF or SHADOW the card shows the
 * old steps, nobody can have edited the rules, and sending them would be a write nobody chose.
 *
 * ⚠️ AN ACCOUNT ON THE BUILT-IN DEFAULTS STAYS ON THEM until somebody edits the rules: a save that
 * only moved the service line would otherwise store the defaults as the account's own rows, and a
 * later change to the built-in default would silently stop reaching it.
 */
export const selectionRulesForSave = (
  form: ISelectionRulesForm | null
): ISelectionRulesSave | undefined => {
  if (!form || form.mode !== "ON") return undefined;
  const body = toRulesSave(form);
  if (!form.configured && isEqual(body, form.loaded)) return undefined;
  return body;
};

// ---------------------------------------------------------------------------
// Validation — spec §2. Saving is never blocked for anything else.
// ---------------------------------------------------------------------------

export interface IRuleErrors {
  scope?: string;
}

/** A rule's own errors, in the dialog's words. Empty object = valid. */
export const validateRule = (rule: ISelectionRuleForm): IRuleErrors => {
  const errors: IRuleErrors = {};
  if (
    !rule.isDefault &&
    rule.payerIds.length === 0 &&
    normaliseKeywords(rule.planKeywords).length === 0
  ) {
    errors.scope =
      "Choose at least one payer or enter a plan name — a rule for every payer and plan is Default.";
  }
  // CLE-2895 — no priority check: a rule with none still answers through the implicit catch-all.
  return errors;
};

export const ruleIsValid = (rule: ISelectionRuleForm): boolean =>
  Object.keys(validateRule(rule)).length === 0;

/** The whole step, for the card's Save button: every rule valid and at most one Default per tab. */
export const selectionRulesAreValid = (
  form: ISelectionRulesForm | null
): boolean => {
  if (!form || form.mode !== "ON") return true;
  return (["shared", "professional"] as RuleScope[]).every(
    (scope) =>
      form[scope].filter((rule) => rule.isDefault).length <= 1 &&
      form[scope].every(ruleIsValid)
  );
};
